import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch, useSelector } from "react-redux";
import { useAdminUsers } from "../../hooks/useAdminUsers";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import AdminUserFilters from "../AdminUserFilters/AdminUserFilters";
import { Button } from "@/components/Button/Button";
import { AdminUser } from "../../api/fetchAdminUsers";
import { createAdminUserColumns } from "../AdminUserColumns/AdminUserColumns";
import DataTable from "@/components/Datatable/Datatable";
import { SyncLogsModal } from "../SyncLogsModal/SyncLogsModal";
import { useToast } from "@/components/ui/use-toast";
import { triggerEmployeeSync } from "@/api/remoteSync/remoteSyncApi";
import {
  addSyncingEmployee,
  selectSyncingEmployeeIds,
} from "../../slices/adminUsersSlice";
import { EmployeeSyncSubscriptions } from "../../hooks/useEmployeeSyncSubscriptions";
import { startCrossAppImpersonation } from "@/api/impersonation/impersonationApi";

const AdminUserList: React.FC = () => {
  const {
    users,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
    filters,
  } = useAdminUsers();

  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const { toast } = useToast();

  const syncingEmployeeIds = useSelector(selectSyncingEmployeeIds);

  const [selectedUserForSyncLogs, setSelectedUserForSyncLogs] =
    useState<AdminUser | null>(null);
  const [isSyncLogsModalOpen, setIsSyncLogsModalOpen] = useState(false);

  const handleEdit = useCallback(
    (uuid: string) => {
      navigate(`/admin/users/${uuid}/edit`);
    },
    [navigate],
  );

  const handleImpersonateUser = useCallback(
    async (employeeUuid: string) => {
      await startCrossAppImpersonation(employeeUuid);
      await getAccessTokenSilently({ cacheMode: "off" });
      localStorage.removeItem("selectedCompanyUuid");
      navigate(0);
    },
    [navigate, getAccessTokenSilently],
  );

  const handleViewSyncLogs = useCallback((user: AdminUser) => {
    setSelectedUserForSyncLogs(user);
    setIsSyncLogsModalOpen(true);
  }, []);

  const handleSyncUser = useCallback(
    async (user: AdminUser) => {
      if (!user.employeeId) {
        toast({
          title: "Error",
          description: "Employee ID not found for this user.",
          variant: "destructive",
        });
        return;
      }

      try {
        await triggerEmployeeSync(user.employeeId);
        // Start tracking this employee for real-time sync updates
        dispatch(addSyncingEmployee(user.employeeId));
        toast({
          title: "Sync Initiated",
          description: `Sync started for ${user.firstName} ${user.lastName}`,
        });
      } catch (error) {
        toast({
          title: "Sync Failed",
          description: "Failed to initiate sync. Please try again.",
          variant: "destructive",
        });
      }
    },
    [toast, dispatch],
  );

  const columns = useMemo(
    () =>
      createAdminUserColumns({
        handleEdit,
        handleImpersonateUser,
        handleViewSyncLogs,
        handleSyncUser,
        syncingEmployeeIds,
      }),
    [
      handleEdit,
      handleImpersonateUser,
      handleViewSyncLogs,
      handleSyncUser,
      syncingEmployeeIds,
    ],
  );

  return (
    <MainPageWrapper
      actions={
        <Button onClick={() => navigate("/admin/users/create")}>
          Create User
        </Button>
      }
      error={error}
      title="User Management"
    >
      <AdminUserFilters
        filters={filters}
        onFilterChange={(
          filterKey: string,
          value: string | number | undefined,
        ) => {
          handleFilterChange(filterKey, value);
          handlePaginationChange({
            pageIndex: 0,
            pageSize: pagination.pageSize,
          });
        }}
      />

      <div className="relative">
        <DataTable<AdminUser>
          columns={columns}
          data={users}
          error={error}
          loading={loading}
          noDataMessage="No users found"
          onPageChange={(newPageIndex, newPageSize) =>
            handlePaginationChange({
              pageIndex: newPageIndex,
              pageSize: newPageSize,
            })
          }
          onSortingChange={handleSortingChange}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          rowKeyExtractor={(item) => item.id}
          sorting={sorting}
          totalCount={totalCount}
        />
      </div>

      <SyncLogsModal
        employeeId={selectedUserForSyncLogs?.employeeId ?? null}
        isOpen={isSyncLogsModalOpen}
        onClose={() => setIsSyncLogsModalOpen(false)}
        user={selectedUserForSyncLogs}
      />

      {/* Subscription handlers for real-time sync status updates */}
      <EmployeeSyncSubscriptions />
    </MainPageWrapper>
  );
};

export default AdminUserList;
